import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Mail, Send, Inbox, RefreshCw, Loader2, Trash2, Reply,
  ChevronLeft, Clock, Users, Settings, Plus, Bot, UserPlus, 
  Building2, Search, CheckCircle, Sparkles, Server, Key,
  Check, X, MailCheck, Eye, EyeOff, FolderOpen, Layers,
  Archive, AlertCircle, Briefcase, Newspaper, Receipt, Lock,
  Wand2, FolderInput, ArrowRightLeft, Languages, Globe,
  Download, PlayCircle, StopCircle, Percent, Bell, MessageSquare, Phone,
  AlertTriangle, XCircle, CheckCircle2, History, ArrowDownLeft, ArrowUpRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SafeHtml } from "@/components/SafeHtml";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapowanie kodów języków na nazwy
const LANGUAGE_NAMES = {
  pl: "Polski",
  en: "Angielski",
  de: "Niemiecki",
  fr: "Francuski",
  es: "Hiszpański",
  it: "Włoski",
  ru: "Rosyjski",
  uk: "Ukraiński",
  cs: "Czeski",
  nl: "Holenderski"
};

// Niestandardowe foldery - stała lista
const CUSTOM_FOLDERS = [
  { id: "inbox", name: "Skrzynka zbiorcza", icon: "inbox" },
  { id: "zlecenia", name: "Zlecenia reklamodawcy", icon: "briefcase" },
  { id: "newsy", name: "Newsy i redakcja", icon: "newspaper" },
  { id: "faktury", name: "Faktury i płatności", icon: "receipt" },
  { id: "inne", name: "Inne", icon: "folder" },
  { id: "poufne", name: "poufne", icon: "lock" },
  { id: "powiadomienia", name: "Powiadomienia", icon: "bell" },
  { id: "spam", name: "Spam i smieci", icon: "trash" },
  { id: "wyslane", name: "Wysłane", icon: "send" },
  { id: "odbite", name: "Odbite", icon: "alert" },
];

// Helper: ikona folderu
const getFolderIcon = (folderId) => {
  switch(folderId) {
    case "inbox": return <Inbox className="w-3.5 h-3.5" />;
    case "zlecenia": return <Briefcase className="w-3.5 h-3.5" />;
    case "newsy": return <Newspaper className="w-3.5 h-3.5" />;
    case "faktury": return <Receipt className="w-3.5 h-3.5" />;
    case "poufne": return <Lock className="w-3.5 h-3.5" />;
    case "powiadomienia": return <Bell className="w-3.5 h-3.5" />;
    case "spam": return <Trash2 className="w-3.5 h-3.5" />;
    case "wyslane": return <Send className="w-3.5 h-3.5" />;
    case "odbite": return <AlertTriangle className="w-3.5 h-3.5 text-red-500" />;
    default: return <FolderOpen className="w-3.5 h-3.5" />;
  }
};

// Funkcja formatująca treść emaila
const formatEmailBody = (body) => {
  if (!body) return "(pusta treść)";
  
  let formatted = body;
  
  // Jeśli to nie jest HTML (brak tagów), konwertuj na HTML
  if (!/<[a-z][\s\S]*>/i.test(formatted)) {
    // Zamień podwójne nowe linie na akapity
    formatted = formatted
      .split(/\n\n+/)
      .map(para => `<p style="margin-bottom: 12px;">${para.replace(/\n/g, '<br/>')}</p>`)
      .join('');
  } else {
    // Dla HTML - upewnij się że nowe linie są zachowane
    formatted = formatted
      .replace(/\r\n/g, '\n')
      .replace(/\n/g, '<br/>');
  }
  
  // Formatowanie cytatów (linie zaczynające się od >)
  formatted = formatted.replace(
    /(&gt;|>)([^<\n]+)/g, 
    '<span style="color: #666; border-left: 3px solid #ddd; padding-left: 10px; display: block; margin: 5px 0;">$2</span>'
  );
  
  // Podświetl linki
  formatted = formatted.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener" style="color: #3b82f6; text-decoration: underline;">$1</a>'
  );
  
  // Formatuj stopki/podpisy (---  lub __ lub pozdrawiam itp)
  formatted = formatted.replace(
    /(---+|___+|--\s*$)/g,
    '<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 15px 0;"/>'
  );
  
  return formatted;
};

export default function MailClient() {
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [bounced, setBounced] = useState([]);
  const [sentStats, setSentStats] = useState({ total_sent: 0, total_bounced: 0, auto_replies: 0, sent_today: 0, delivery_rate: 100 });
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedSentEmail, setSelectedSentEmail] = useState(null); // Dla podglądu wysłanych
  const [account, setAccount] = useState("all"); // "all" = skrzynka zbiorcza
  const [currentFolder, setCurrentFolder] = useState("inbox"); // Niestandardowy folder
  const [activeTab, setActiveTab] = useState("inbox");
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [compose, setCompose] = useState({ to: "", subject: "", body: "", from_account: "bok" });
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnlyAnswered, setShowOnlyAnswered] = useState(false);
  
  // AI Analysis
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [draftingReply, setDraftingReply] = useState(false);
  
  // CRM
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [newContact, setNewContact] = useState({ email: "", name: "", company: "", phone: "", category: "lead", notes: "" });
  const [creatingAccount, setCreatingAccount] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null); // Do wyświetlania historii
  const [contactMessages, setContactMessages] = useState([]); // Historia wiadomości kontaktu
  const [loadingContactMessages, setLoadingContactMessages] = useState(false);

  // Account config
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newAccount, setNewAccount] = useState({
    email: "", name: "", imap_server: "", imap_port: 993,
    smtp_server: "", smtp_port: 587, username: "", password: ""
  });

  // Pagination
  const [totalEmails, setTotalEmails] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 50;

  // AI Sorting
  const [sortingDialogOpen, setSortingDialogOpen] = useState(false);
  const [sortingConfig, setSortingConfig] = useState({ prompt: "", enabled: true, auto_sort: false });
  const [sorting, setSorting] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [emailToMove, setEmailToMove] = useState(null);

  // Translation
  const [translating, setTranslating] = useState(false);
  const [translatedBody, setTranslatedBody] = useState(null);

  // Background Jobs
  const [jobStatus, setJobStatus] = useState(null);
  const [jobDialogOpen, setJobDialogOpen] = useState(false);

  // Auto Reply
  const [autoReplyConfig, setAutoReplyConfig] = useState({ 
    enabled: true, 
    signature: { name: "", position: "", phone: "", telegram: "", whatsapp: "", email: "" },
    offer_prompt: ""
  });
  const [autoReplyDialogOpen, setAutoReplyDialogOpen] = useState(false);
  const [generatingReply, setGeneratingReply] = useState(false);
  const [generatedReply, setGeneratedReply] = useState(null);

  // Filtry pomijania
  const [skipFilters, setSkipFilters] = useState({ enabled: true, emails: [], domains: [], keywords: [] });
  const [skipFiltersDialogOpen, setSkipFiltersDialogOpen] = useState(false);
  const [newFilter, setNewFilter] = useState({ type: "email", value: "" });

  // Auto Reply Job
  const [autoReplyJobStatus, setAutoReplyJobStatus] = useState(null);
  const [autoReplyJobDialogOpen, setAutoReplyJobDialogOpen] = useState(false);
  const [folderStats, setFolderStats] = useState({ unreplied: 0, total: 0 });

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mail/accounts`);
      setAccounts(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchSortingConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mail/sorting-config`);
      setSortingConfig(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchAutoReplyConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mail/auto-reply-config`);
      setAutoReplyConfig(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchSkipFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mail/skip-filters`);
      setSkipFilters(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const handleAddFilter = async () => {
    if (!newFilter.value.trim()) { toast.error("Podaj wartość filtra"); return; }
    try {
      await axios.post(`${API}/mail/skip-filters/add`, newFilter);
      toast.success(`Dodano filtr: ${newFilter.value}`);
      setNewFilter({ ...newFilter, value: "" });
      fetchSkipFilters();
    } catch (err) { toast.error("Błąd dodawania filtra"); }
  };

  const handleRemoveFilter = async (type, value) => {
    try {
      await axios.delete(`${API}/mail/skip-filters/remove`, { data: { type, value } });
      toast.success(`Usunięto filtr: ${value}`);
      fetchSkipFilters();
    } catch (err) { toast.error("Błąd usuwania filtra"); }
  };

  const fetchJobStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/mail/jobs/status`);
      if (!res.data.error) {
        setJobStatus(res.data);
      }
    } catch (err) { console.error(err); }
  }, []);

  // Funkcja tłumaczenia
  const handleTranslateEmail = async (email) => {
    setTranslating(true);
    setTranslatedBody(null);
    try {
      const res = await axios.post(`${API}/mail/translate`, {
        subject: email.subject,
        body: email.body?.replace(/<[^>]*>/g, '') || ""
      });
      if (res.data.success) {
        setTranslatedBody(res.data.translated_body);
        toast.success("Przetłumaczono na polski");
      } else {
        toast.error(res.data.error || "Błąd tłumaczenia");
      }
    } catch (err) { 
      toast.error("Błąd tłumaczenia"); 
    }
    finally { setTranslating(false); }
  };

  // Uruchom zadanie pobierania i sortowania
  const handleStartFetchSort = async () => {
    try {
      const res = await axios.post(`${API}/mail/jobs/start-fetch-sort`, { account_ids: ["all"] });
      if (res.data.success) {
        toast.success("Zadanie uruchomione w tle");
        setJobStatus({ ...res.data, status: "running", progress: 0 });
        setJobDialogOpen(true);
      } else {
        toast.error(res.data.error || "Blad uruchamiania zadania");
        if (res.data.job_id) {
          fetchJobStatus();
          setJobDialogOpen(true);
        }
      }
    } catch (err) { toast.error("Blad uruchamiania zadania"); }
  };

  // Auto-refresh job status
  useEffect(() => {
    if (jobDialogOpen && jobStatus && (jobStatus.status === "running" || jobStatus.status === "pending")) {
      const interval = setInterval(fetchJobStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [jobDialogOpen, jobStatus, fetchJobStatus]);

  // Generuj automatyczną odpowiedź AI
  const handleGenerateAutoReply = async (email) => {
    setGeneratingReply(true);
    setGeneratedReply(null);
    try {
      const res = await axios.post(`${API}/mail/generate-reply`, {
        subject: email.subject,
        body: email.body?.replace(/<[^>]*>/g, '') || "",
        from: email.from,
        language: email.detected_language || "pl",
        email_id: email.id  // Przekazujemy email_id do pobrania historii wątku
      });
      if (res.data.success) {
        setGeneratedReply(res.data.reply);
        
        // Pokaż informację o wątkowaniu jeśli to kontynuacja
        if (res.data.is_continuation && res.data.thread_messages_count > 1) {
          toast.success(`Wygenerowano odpowiedź AI (kontynuacja wątku - ${res.data.thread_messages_count} wiadomości)`);
        } else {
          toast.success("Wygenerowano odpowiedź AI");
        }
        
        // Automatycznie dodaj kontakt do CRM
        if (res.data.contact_extracted) {
          const contact = res.data.contact_extracted;
          if (contact.email) {
            await axios.post(`${API}/mail/auto-add-to-crm`, {
              ...contact,
              source_email_id: email.id
            });
            toast.success("Dodano kontakt do CRM");
          }
        }
      } else {
        toast.error(res.data.error || "Błąd generowania odpowiedzi");
      }
    } catch (err) { 
      toast.error("Błąd generowania odpowiedzi"); 
    }
    finally { setGeneratingReply(false); }
  };

  // Pobierz statystyki folderu
  const fetchFolderStats = async (folder) => {
    try {
      const res = await axios.get(`${API}/mail/folder-stats?folder=${folder}`);
      setFolderStats(res.data);
    } catch (err) { console.error(err); }
  };

  // Pobierz status auto-reply job
  const fetchAutoReplyJobStatus = async () => {
    try {
      const res = await axios.get(`${API}/mail/jobs/auto-reply-status`);
      if (!res.data.error) {
        setAutoReplyJobStatus(res.data);
      }
    } catch (err) { console.error(err); }
  };

  // Uruchom masowe auto-reply
  const handleStartAutoReplyAll = async () => {
    try {
      const res = await axios.post(`${API}/mail/jobs/start-auto-reply`, {
        folder: currentFolder,
        limit: 50
      });
      if (res.data.success) {
        toast.success(`Uruchomiono auto-reply dla ${res.data.emails_to_process} maili`);
        setAutoReplyJobStatus(res.data);
        setAutoReplyJobDialogOpen(true);
      } else {
        toast.error(res.data.error || "Błąd uruchamiania");
        if (res.data.job_id) {
          fetchAutoReplyJobStatus();
          setAutoReplyJobDialogOpen(true);
        }
      }
    } catch (err) { toast.error("Błąd uruchamiania auto-reply"); }
  };

  // Auto-refresh auto-reply job status
  useEffect(() => {
    if (autoReplyJobDialogOpen && autoReplyJobStatus && 
        (autoReplyJobStatus.status === "running" || autoReplyJobStatus.status === "pending")) {
      const interval = setInterval(fetchAutoReplyJobStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [autoReplyJobDialogOpen, autoReplyJobStatus]);

  // Fetch folder stats when folder changes
  useEffect(() => {
    if (currentFolder && currentFolder !== "inbox" && currentFolder !== "wyslane") {
      fetchFolderStats(currentFolder);
    }
  }, [currentFolder]);

  // Zapisz konfigurację auto-reply
  const handleSaveAutoReplyConfig = async () => {
    try {
      await axios.put(`${API}/mail/auto-reply-config`, autoReplyConfig);
      toast.success("Konfiguracja zapisana");
      setAutoReplyDialogOpen(false);
    } catch (err) { toast.error("Błąd zapisu konfiguracji"); }
  };

  // Użyj wygenerowanej odpowiedzi do Reply
  const handleUseGeneratedReply = (email) => {
    if (generatedReply) {
      setCompose({
        to: email.from?.match(/[\w\.-]+@[\w\.-]+\.\w+/)?.[0] || email.from,
        subject: `Re: ${email.subject}`,
        body: generatedReply,
        from_account: account !== "all" ? account : "bok",
        reply_to_message_id: email.message_id
      });
      setReplyOpen(true);
    }
  };

  const fetchData = useCallback(async (loadMore = false) => {
    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setCurrentPage(0);
    }
    
    try {
      const offset = loadMore ? (currentPage + 1) * pageSize : 0;
      
      // Pobierz maile z odpowiedniego folderu
      let inboxUrl;
      if (currentFolder === "inbox") {
        // Skrzynka zbiorcza - wszystkie maile
        inboxUrl = `${API}/mail/all?limit=${pageSize}&offset=${offset}`;
      } else if (currentFolder === "wyslane") {
        // Wyslane - z osobnego endpointu
        const [sentRes, statsRes] = await Promise.all([
          axios.get(`${API}/mail/sent?limit=200`),
          axios.get(`${API}/mail/stats/sent`)
        ]);
        setSent(sentRes.data || []);
        setSentStats(statsRes.data || {});
        setInbox([]); // Wyczyść inbox
        setTotalEmails(sentRes.data?.length || 0);
        setLoading(false);
        return;
      } else if (currentFolder === "odbite") {
        // Maile odbite (bounced)
        const [bouncedRes, statsRes] = await Promise.all([
          axios.get(`${API}/mail/bounced`),
          axios.get(`${API}/mail/stats/sent`)
        ]);
        setBounced(bouncedRes.data || []);
        setSentStats(statsRes.data || {});
        setInbox([]); // Wyczyść inbox
        setTotalEmails(bouncedRes.data?.length || 0);
        setLoading(false);
        return;
      } else {
        // Posortowane foldery - z bazy danych
        inboxUrl = `${API}/mail/sorted?folder=${currentFolder}&limit=${pageSize}&offset=${offset}${showOnlyAnswered ? "&only_answered=true" : ""}`;
      }
      
      const [inboxRes, contactsRes] = await Promise.all([
        axios.get(inboxUrl),
        axios.get(`${API}/crm/contacts`),
      ]);
      
      const inboxData = inboxRes.data;
      if (loadMore) {
        setInbox(prev => [...prev, ...(inboxData.emails || [])]);
        setCurrentPage(prev => prev + 1);
      } else {
        setInbox(inboxData.emails || []);
      }
      setTotalEmails(inboxData.total || 0);
      setContacts(contactsRes.data);
    } catch (err) { 
      console.error(err); 
      if (err.response?.status === 500) {
        toast.error("Blad polaczenia z serwerem poczty");
      }
    }
    finally { 
      setLoading(false); 
      setLoadingMore(false);
    }
  }, [currentFolder, currentPage, showOnlyAnswered]);

  const loadMoreEmails = () => {
    fetchData(true);
  };

  useEffect(() => { 
    fetchAccounts(); 
    fetchSortingConfig();
    fetchAutoReplyConfig();
    fetchSkipFilters();
  }, [fetchAccounts, fetchSortingConfig, fetchAutoReplyConfig, fetchSkipFilters]);
  
  useEffect(() => { 
    fetchData(); 
  }, [currentFolder, showOnlyAnswered]); // Reload when folder or filter changes

  // CLEANUP: Wyczyść stany podglądu gdy zmienia się wybrana wiadomość
  useEffect(() => {
    // Reset wszystkich stanów podglądu przy zmianie wiadomości
    setGeneratedReply(null);
    setTranslatedBody(null);
    setAnalysis(null);
  }, [selectedEmail?.id]); // Trigger tylko gdy zmieni się ID wiadomości

  // AI Sorting functions
  const handleSaveSortingConfig = async () => {
    try {
      await axios.put(`${API}/mail/sorting-config`, sortingConfig);
      toast.success("Konfiguracja sortowania zapisana");
      setSortingDialogOpen(false);
    } catch (err) { toast.error("Blad zapisu konfiguracji"); }
  };

  const handleSortSelectedEmails = async () => {
    if (inbox.length === 0) {
      toast.error("Brak emaili do posortowania");
      return;
    }
    setSorting(true);
    try {
      const emailsToSort = inbox.slice(0, 20).map(em => ({
        email_id: em.id,
        subject: em.subject,
        body: em.body?.replace(/<[^>]*>/g, '').substring(0, 1500),
        from: em.from,
        account_id: em.account_id || "bok"
      }));
      
      const res = await axios.post(`${API}/mail/sort-batch`, { emails: emailsToSort });
      if (res.data.sorted_count > 0) {
        toast.success(`Posortowano ${res.data.sorted_count} emaili`);
        fetchData();
      } else {
        toast.info("Brak emaili do posortowania");
      }
    } catch (err) { 
      toast.error("Blad sortowania AI"); 
    }
    finally { setSorting(false); }
  };

  const handleMoveEmail = async (targetFolder) => {
    if (!emailToMove) return;
    try {
      const res = await axios.post(`${API}/mail/move-to-folder`, {
        email_id: emailToMove.id,
        folder: targetFolder
      });
      
      const folderName = CUSTOM_FOLDERS.find(f => f.id === targetFolder)?.name || targetFolder;
      
      if (res.data.similar_moved > 0) {
        toast.success(`Przeniesiono do: ${folderName}. AI nauczył się i przeniósł ${res.data.similar_moved} podobnych maili od ${res.data.sender}`, {
          duration: 5000
        });
      } else if (res.data.learned_rule) {
        toast.success(`Przeniesiono do: ${folderName}. AI zapamiętał: maile od ${res.data.sender} → ${folderName}`);
      } else {
        toast.success(`Przeniesiono do: ${folderName}`);
      }
      
      setMoveDialogOpen(false);
      setEmailToMove(null);
      fetchData();
    } catch (err) { toast.error("Błąd przenoszenia"); }
  };

  const handleSend = async () => {
    if (!compose.to || !compose.subject) { toast.error("Podaj adresata i temat"); return; }
    setSending(true);
    try {
      const payload = {
        ...compose,
        original_message_id: selectedEmail?.message_id
      };
      const res = await axios.post(`${API}/mail/send`, payload);
      if (res.data.success) {
        toast.success("Email wyslany!");
        setComposeOpen(false);
        setReplyOpen(false);
        setCompose({ to: "", subject: "", body: "", from_account: account });
        fetchData();
      } else {
        toast.error(res.data.error || "Blad wysylki");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Blad wysylki"); }
    finally { setSending(false); }
  };

  const handleReply = (email) => {
    const replyTo = email.from.includes("<") ? email.from.match(/<(.+)>/)?.[1] || email.from : email.from;
    setCompose({
      to: replyTo,
      subject: email.subject.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
      body: `\n\n---\nW dniu ${email.date} ${email.from} napisal(a):\n${email.body?.replace(/<[^>]*>/g, '').substring(0, 500)}...`,
      from_account: account,
      original_id: email.id,
      original_message_id: email.message_id
    });
    setReplyOpen(true);
  };

  const handleDelete = async (emailId) => {
    try {
      await axios.delete(`${API}/mail/${emailId}?account=${account}`);
      toast.success("Email przeniesiony do kosza");
      setSelectedEmail(null);
      fetchData();
    } catch (err) { toast.error("Blad usuwania"); }
  };

  // AI Functions
  const handleAnalyzeEmail = async (email) => {
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const res = await axios.post(`${API}/mail/ai/analyze`, {
        content: email.body,
        subject: email.subject,
        from: email.from
      });
      setAnalysis(res.data);
    } catch (err) { toast.error("Blad analizy AI"); }
    finally { setAnalyzing(false); }
  };

  const handleDraftReply = async (email) => {
    setDraftingReply(true);
    try {
      const res = await axios.post(`${API}/mail/ai/draft-reply`, {
        content: email.body,
        subject: email.subject,
        from: email.from,
        tone: "profesjonalny"
      });
      if (!res.data.error) {
        const replyTo = email.from.includes("<") ? email.from.match(/<(.+)>/)?.[1] || email.from : email.from;
        setCompose({
          to: replyTo,
          subject: email.subject.startsWith("Re:") ? email.subject : `Re: ${email.subject}`,
          body: res.data.draft,
          from_account: account,
          original_id: email.id,
          original_message_id: email.message_id
        });
        setReplyOpen(true);
        toast.success("Wygenerowano propozycje odpowiedzi");
      } else {
        toast.error("Blad generowania odpowiedzi");
      }
    } catch (err) { toast.error("Blad AI"); }
    finally { setDraftingReply(false); }
  };

  const handleCreateLeadFromEmail = async (email) => {
    try {
      const res = await axios.post(`${API}/mail/lead-from-email`, {
        from: email.from,
        subject: email.subject,
        body: email.body
      });
      if (res.data.success) {
        toast.success(res.data.is_new ? "Dodano nowy lead do CRM" : "Zaktualizowano istniejacy kontakt");
        fetchData();
      }
    } catch (err) { toast.error("Blad dodawania leada"); }
  };

  // Pobierz historię wiadomości kontaktu
  const handleViewContactMessages = async (contact) => {
    setSelectedContact(contact);
    setLoadingContactMessages(true);
    try {
      const res = await axios.get(`${API}/crm/contacts/${contact.id}/messages`);
      setContactMessages(res.data.messages || []);
    } catch (err) {
      toast.error("Błąd pobierania historii");
      setContactMessages([]);
    } finally {
      setLoadingContactMessages(false);
    }
  };

  // CRM Functions
  const handleAddContact = async () => {
    if (!newContact.email) { toast.error("Podaj email"); return; }
    try {
      await axios.post(`${API}/crm/contacts`, newContact);
      toast.success("Dodano kontakt");
      setContactDialogOpen(false);
      setNewContact({ email: "", name: "", company: "", phone: "", category: "lead", notes: "" });
      fetchData();
    } catch (err) { toast.error("Blad dodawania kontaktu"); }
  };

  const handleCreateAccountForContact = async (contactId) => {
    setCreatingAccount(contactId);
    try {
      const res = await axios.post(`${API}/crm/contacts/${contactId}/create-account`);
      if (res.data.success) {
        toast.success(`Konto utworzone! Haslo: ${res.data.temp_password}`, { duration: 10000 });
        fetchData();
      }
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Blad tworzenia konta"); 
    }
    finally { setCreatingAccount(null); }
  };

  // Account Management
  const handleTestConnection = async () => {
    if (!newAccount.imap_server || !newAccount.username || !newAccount.password) {
      toast.error("Wypelnij wszystkie pola");
      return;
    }
    setTestingConnection(true);
    try {
      const res = await axios.post(`${API}/mail/accounts/test`, newAccount);
      if (res.data.imap?.success && res.data.smtp?.success) {
        toast.success("Polaczenie IMAP i SMTP OK!");
      } else {
        const errors = [];
        if (!res.data.imap?.success) errors.push(`IMAP: ${res.data.imap?.error}`);
        if (!res.data.smtp?.success) errors.push(`SMTP: ${res.data.smtp?.error}`);
        toast.error(errors.join("\n"));
      }
    } catch (err) { toast.error("Blad testu polaczenia"); }
    finally { setTestingConnection(false); }
  };

  const handleAddMailAccount = async () => {
    if (!newAccount.email || !newAccount.imap_server || !newAccount.username || !newAccount.password) {
      toast.error("Wypelnij wszystkie wymagane pola");
      return;
    }
    try {
      await axios.post(`${API}/mail/accounts`, newAccount);
      toast.success("Konto dodane!");
      setAccountDialogOpen(false);
      setNewAccount({ email: "", name: "", imap_server: "", imap_port: 993, smtp_server: "", smtp_port: 587, username: "", password: "" });
      fetchAccounts();
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Blad dodawania konta"); 
    }
  };

  const handleDeleteMailAccount = async (accountId) => {
    if (!window.confirm("Usunac konto email?")) return;
    try {
      await axios.delete(`${API}/mail/accounts/${accountId}`);
      toast.success("Konto usuniete");
      fetchAccounts();
      if (account === accountId) setAccount("all");
    } catch (err) { toast.error(err.response?.data?.detail || "Blad usuwania"); }
  };

  const filteredInbox = inbox.filter(em => 
    (!searchQuery || 
    em.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    em.from?.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!showOnlyAnswered || em.is_answered)
  );

  const getCategoryBadge = (category) => {
    const colors = {
      lead: "bg-yellow-100 text-yellow-800",
      client: "bg-green-100 text-green-800",
      partner: "bg-blue-100 text-blue-800",
      other: "bg-gray-100 text-gray-800"
    };
    return colors[category] || colors.other;
  };

  const currentFolderData = CUSTOM_FOLDERS.find(f => f.id === currentFolder) || CUSTOM_FOLDERS[0];

  const isJobRunning = jobStatus && (jobStatus.status === "running" || jobStatus.status === "pending");

  return (
    <div data-testid="mail-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">Poczta</h1>
          <p className="text-sm text-muted-foreground mt-1">Klient poczty z AI i CRM</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { fetchJobStatus(); setJobDialogOpen(true); }}
            className={`gap-1.5 ${isJobRunning ? "text-green-600 border-green-200 hover:bg-green-50" : "text-blue-600 border-blue-200 hover:bg-blue-50"}`}
          >
            {isJobRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {isJobRunning ? `${jobStatus.progress || 0}%` : "Pobierz wszystko"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleSortSelectedEmails} disabled={sorting} className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50">
            {sorting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
            Sortuj AI
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSortingDialogOpen(true)} className="gap-1">
            <Bot className="w-3.5 h-3.5" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAutoReplyDialogOpen(true)} className="gap-1 text-green-600 border-green-200 hover:bg-green-50">
            <MessageSquare className="w-3.5 h-3.5" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSkipFiltersDialogOpen(true)} className="gap-1 text-orange-600 border-orange-200 hover:bg-orange-50" title="Filtry pomijania">
            <AlertCircle className="w-3.5 h-3.5" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchData(false)} className="gap-1">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> 
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAccountDialogOpen(true)} className="gap-1">
            <Settings className="w-3.5 h-3.5" />
          </Button>
          <Button onClick={() => { setCompose({ ...compose, from_account: accounts[0]?.id || "bok" }); setComposeOpen(true); }} className="gap-2 rounded-lg">
            <Send className="w-4 h-4" /> Napisz
          </Button>
        </div>
      </div>

      {/* Auto Reply Config Dialog */}
      <Dialog open={autoReplyDialogOpen} onOpenChange={setAutoReplyDialogOpen}>
        <DialogContent className="sm:max-w-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-500" />
              Konfiguracja automatycznych odpowiedzi
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-medium">Stopka / Podpis maila</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Imie i nazwisko</Label>
                  <Input 
                    placeholder="Jan Kowalski"
                    value={autoReplyConfig.signature?.name || ""}
                    onChange={(e) => setAutoReplyConfig({
                      ...autoReplyConfig, 
                      signature: {...autoReplyConfig.signature, name: e.target.value}
                    })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Stanowisko</Label>
                  <Input 
                    placeholder="Specjalista ds. publikacji"
                    value={autoReplyConfig.signature?.position || ""}
                    onChange={(e) => setAutoReplyConfig({
                      ...autoReplyConfig, 
                      signature: {...autoReplyConfig.signature, position: e.target.value}
                    })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Telefon</Label>
                  <Input 
                    placeholder="+48 123 456 789"
                    value={autoReplyConfig.signature?.phone || ""}
                    onChange={(e) => setAutoReplyConfig({
                      ...autoReplyConfig, 
                      signature: {...autoReplyConfig.signature, phone: e.target.value}
                    })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <Input 
                    placeholder="kontakt@epidemos.pl"
                    value={autoReplyConfig.signature?.email || ""}
                    onChange={(e) => setAutoReplyConfig({
                      ...autoReplyConfig, 
                      signature: {...autoReplyConfig.signature, email: e.target.value}
                    })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Telegram</Label>
                  <Input 
                    placeholder="@username"
                    value={autoReplyConfig.signature?.telegram || ""}
                    onChange={(e) => setAutoReplyConfig({
                      ...autoReplyConfig, 
                      signature: {...autoReplyConfig.signature, telegram: e.target.value}
                    })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">WhatsApp</Label>
                  <Input 
                    placeholder="+48 123 456 789"
                    value={autoReplyConfig.signature?.whatsapp || ""}
                    onChange={(e) => setAutoReplyConfig({
                      ...autoReplyConfig, 
                      signature: {...autoReplyConfig.signature, whatsapp: e.target.value}
                    })}
                  />
                </div>
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label className="font-medium">Prompt dla AI (instrukcje jak odpowiadac)</Label>
              <Textarea 
                value={autoReplyConfig.offer_prompt || ""}
                onChange={(e) => setAutoReplyConfig({...autoReplyConfig, offer_prompt: e.target.value})}
                rows={10}
                className="font-mono text-xs"
                placeholder="Wpisz instrukcje dla AI jak ma odpowiadac na maile..."
              />
              <p className="text-xs text-muted-foreground">
                AI bedzie odpowiadac w jezyku w jakim przyszedl mail. Dane kontaktowe (email, telefon) zostana automatycznie dodane do CRM.
              </p>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={autoReplyConfig.enabled}
                  onChange={(e) => setAutoReplyConfig({...autoReplyConfig, enabled: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Wlacz automatyczne odpowiedzi AI</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoReplyDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleSaveAutoReplyConfig} className="gap-2">
              <Check className="w-4 h-4" /> Zapisz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog filtrów pomijania */}
      <Dialog open={skipFiltersDialogOpen} onOpenChange={setSkipFiltersDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Filtry pomijania maili
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Adresy i domeny z tej listy nie będą otrzymywać automatycznych odpowiedzi AI.
            </p>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Dodaj nowy filtr */}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">Typ filtra</Label>
                <select 
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                  value={newFilter.type}
                  onChange={(e) => setNewFilter({ ...newFilter, type: e.target.value })}
                >
                  <option value="email">Email (dokładny adres)</option>
                  <option value="domain">Domena (np. collaborator.pro)</option>
                  <option value="keyword">Słowo kluczowe (w nadawcy/temacie)</option>
                </select>
              </div>
              <div className="flex-[2]">
                <Label className="text-xs">Wartość</Label>
                <Input 
                  placeholder={newFilter.type === "email" ? "noreply@example.com" : newFilter.type === "domain" ? "example.com" : "notification"}
                  value={newFilter.value}
                  onChange={(e) => setNewFilter({ ...newFilter, value: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddFilter()}
                />
              </div>
              <Button onClick={handleAddFilter} className="gap-1">
                <Plus className="w-4 h-4" /> Dodaj
              </Button>
            </div>

            {/* Lista filtrów */}
            <div className="space-y-3">
              {/* Emails */}
              {skipFilters.emails?.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Adresy email ({skipFilters.emails.length})</Label>
                  <div className="flex flex-wrap gap-1">
                    {skipFilters.emails.map((email) => (
                      <Badge key={email} variant="secondary" className="gap-1 pr-1">
                        <Mail className="w-3 h-3" />
                        {email}
                        <Button variant="ghost" size="sm" className="h-4 w-4 p-0 ml-1 hover:bg-red-100" onClick={() => handleRemoveFilter("email", email)}>
                          <X className="w-3 h-3 text-red-500" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Domeny */}
              {skipFilters.domains?.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Domeny ({skipFilters.domains.length})</Label>
                  <div className="flex flex-wrap gap-1">
                    {skipFilters.domains.map((domain) => (
                      <Badge key={domain} variant="secondary" className="gap-1 pr-1 bg-blue-50">
                        <Globe className="w-3 h-3" />
                        {domain}
                        <Button variant="ghost" size="sm" className="h-4 w-4 p-0 ml-1 hover:bg-red-100" onClick={() => handleRemoveFilter("domain", domain)}>
                          <X className="w-3 h-3 text-red-500" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Słowa kluczowe */}
              {skipFilters.keywords?.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Słowa kluczowe ({skipFilters.keywords.length})</Label>
                  <div className="flex flex-wrap gap-1">
                    {skipFilters.keywords.map((kw) => (
                      <Badge key={kw} variant="secondary" className="gap-1 pr-1 bg-purple-50">
                        <Search className="w-3 h-3" />
                        {kw}
                        <Button variant="ghost" size="sm" className="h-4 w-4 p-0 ml-1 hover:bg-red-100" onClick={() => handleRemoveFilter("keyword", kw)}>
                          <X className="w-3 h-3 text-red-500" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {(!skipFilters.emails?.length && !skipFilters.domains?.length && !skipFilters.keywords?.length) && (
                <div className="text-center py-6 text-muted-foreground">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Brak zdefiniowanych filtrów</p>
                  <p className="text-xs">Dodaj adresy lub domeny, które mają być pomijane</p>
                </div>
              )}
            </div>

            {/* Szybkie dodawanie popularnych filtrów */}
            <div className="border-t pt-4">
              <Label className="text-xs text-muted-foreground mb-2 block">Szybkie dodawanie typowych filtrów:</Label>
              <div className="flex flex-wrap gap-1">
                {["noreply", "no-reply", "notification", "support", "newsletter", "mailer-daemon"].map((kw) => (
                  <Button 
                    key={kw} 
                    variant="outline" 
                    size="sm" 
                    className="text-xs h-7"
                    onClick={() => {
                      setNewFilter({ type: "keyword", value: kw });
                      handleAddFilter();
                    }}
                    disabled={skipFilters.keywords?.includes(kw)}
                  >
                    + {kw}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSkipFiltersDialogOpen(false)}>
              Zamknij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Status Dialog */}
      <Dialog open={jobDialogOpen} onOpenChange={setJobDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-500" />
              Pobieranie i sortowanie maili
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!jobStatus || jobStatus.error === "Nie znaleziono zadania" ? (
              <div className="text-center py-6">
                <p className="text-muted-foreground mb-4">
                  Rozpocznij pobieranie wszystkich maili z serwera i automatyczne sortowanie przez AI.
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  Zadanie zostanie wykonane w tle. Mozesz zamknac to okno i wrocic pozniej.
                </p>
                <Button onClick={handleStartFetchSort} className="gap-2">
                  <PlayCircle className="w-4 h-4" />
                  Rozpocznij pobieranie
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Status:</span>
                  <Badge className={
                    jobStatus.status === "completed" ? "bg-green-100 text-green-800" :
                    jobStatus.status === "running" ? "bg-blue-100 text-blue-800" :
                    jobStatus.status === "error" ? "bg-red-100 text-red-800" :
                    jobStatus.status === "cancelled" ? "bg-gray-100 text-gray-800" :
                    "bg-yellow-100 text-yellow-800"
                  }>
                    {jobStatus.status === "completed" ? "Zakonczone" :
                     jobStatus.status === "running" ? "W trakcie" :
                     jobStatus.status === "error" ? "Blad" :
                     jobStatus.status === "cancelled" ? "Anulowane" :
                     "Oczekuje"}
                  </Badge>
                </div>
                
                {(jobStatus.status === "running" || jobStatus.status === "completed") && (
                  <>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Postep:</span>
                        <span className="font-medium">{jobStatus.progress || 0}%</span>
                      </div>
                      <Progress value={jobStatus.progress || 0} className="h-2" />
                    </div>
                    
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-xl font-bold text-foreground">{jobStatus.total_emails || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Wszystkich</p>
                      </div>
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-xl font-bold text-blue-600">{jobStatus.processed || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Przetworzonych</p>
                      </div>
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-xl font-bold text-green-600">{jobStatus.sorted || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Nowych</p>
                      </div>
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-xl font-bold text-gray-500">{jobStatus.skipped || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Pominiętych</p>
                      </div>
                    </div>
                    
                    {(jobStatus.skipped || 0) > 0 && (
                      <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                        ℹ️ Pominięto {jobStatus.skipped} maili, które były już wcześniej posortowane.
                      </p>
                    )}
                  </>
                )}
                
                {jobStatus.started_at && (
                  <p className="text-xs text-muted-foreground">
                    Rozpoczeto: {new Date(jobStatus.started_at).toLocaleString()}
                  </p>
                )}
                {jobStatus.completed_at && (
                  <p className="text-xs text-green-600">
                    Zakonczone: {new Date(jobStatus.completed_at).toLocaleString()}
                  </p>
                )}
                
                {jobStatus.errors && jobStatus.errors.length > 0 && (
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-xs font-medium text-red-700 mb-1">Bledy ({jobStatus.errors.length}):</p>
                    <div className="max-h-24 overflow-y-auto">
                      {jobStatus.errors.slice(0, 5).map((err, i) => (
                        <p key={i} className="text-xs text-red-600">{err}</p>
                      ))}
                    </div>
                  </div>
                )}
                
                {jobStatus.error && (
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-xs text-red-700">{jobStatus.error}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {isJobRunning && (
              <Button variant="outline" onClick={async () => {
                await axios.post(`${API}/mail/jobs/cancel`, { job_id: jobStatus.id });
                fetchJobStatus();
              }} className="gap-1 text-red-600">
                <StopCircle className="w-4 h-4" /> Anuluj
              </Button>
            )}
            {jobStatus && jobStatus.status === "completed" && (
              <Button onClick={handleStartFetchSort} className="gap-2">
                <PlayCircle className="w-4 h-4" />
                Uruchom ponownie
              </Button>
            )}
            <Button variant="outline" onClick={() => setJobDialogOpen(false)}>Zamknij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto Reply All Job Dialog */}
      <Dialog open={autoReplyJobDialogOpen} onOpenChange={setAutoReplyJobDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-500" />
              Automatyczne odpowiedzi AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!autoReplyJobStatus || autoReplyJobStatus.error === "Nie znaleziono zadania auto-reply" ? (
              <div className="text-center py-6">
                <p className="text-muted-foreground mb-2">
                  Folder: <strong>{CUSTOM_FOLDERS.find(f => f.id === currentFolder)?.name}</strong>
                </p>
                <p className="text-muted-foreground mb-4">
                  Maili bez odpowiedzi: <strong className="text-orange-600">{folderStats.unreplied}</strong> z {folderStats.total}
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  AI wygeneruje i wysle odpowiedzi na wszystkie maile bez odpowiedzi.<br/>
                  Limit: 1 mail na minute (aby uniknac spamu).
                </p>
                <Button 
                  onClick={handleStartAutoReplyAll} 
                  disabled={folderStats.unreplied === 0}
                  className="gap-2"
                >
                  <PlayCircle className="w-4 h-4" />
                  Odpowiedz wszystkim ({folderStats.unreplied})
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Status:</span>
                  <Badge className={
                    autoReplyJobStatus.status === "completed" ? "bg-green-100 text-green-800" :
                    autoReplyJobStatus.status === "running" ? "bg-blue-100 text-blue-800" :
                    autoReplyJobStatus.status === "error" ? "bg-red-100 text-red-800" :
                    "bg-yellow-100 text-yellow-800"
                  }>
                    {autoReplyJobStatus.status === "completed" ? "Zakonczone" :
                     autoReplyJobStatus.status === "running" ? "W trakcie" :
                     autoReplyJobStatus.status === "error" ? "Blad" :
                     "Oczekuje"}
                  </Badge>
                </div>
                
                {(autoReplyJobStatus.status === "running" || autoReplyJobStatus.status === "completed") && (
                  <>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Postęp:</span>
                        <span className="font-medium">{autoReplyJobStatus.progress || 0}%</span>
                      </div>
                      <Progress value={autoReplyJobStatus.progress || 0} className="h-2" />
                    </div>
                    
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-xl font-bold text-foreground">{autoReplyJobStatus.total_emails || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Wszystkich</p>
                      </div>
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-xl font-bold text-green-600">{autoReplyJobStatus.sent || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Wysłanych</p>
                      </div>
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-xl font-bold text-red-600">{autoReplyJobStatus.failed || 0}</p>
                        <p className="text-[10px] text-muted-foreground">Błędów</p>
                      </div>
                      <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="text-xl font-bold text-orange-600">
                          {(autoReplyJobStatus.total_emails || 0) - (autoReplyJobStatus.processed || 0)}
                        </p>
                        <p className="text-[10px] text-muted-foreground">Pozostało</p>
                      </div>
                    </div>
                    
                    {autoReplyJobStatus.status === "running" && (
                      <p className="text-xs text-muted-foreground text-center">
                        Szacowany czas: ~{(autoReplyJobStatus.total_emails || 0) - (autoReplyJobStatus.processed || 0)} min
                        <br/>(1 mail na minutę)
                      </p>
                    )}
                  </>
                )}
                
                {autoReplyJobStatus.errors && autoReplyJobStatus.errors.length > 0 && (
                  <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-xs font-medium text-red-700 mb-1">Błędy ({autoReplyJobStatus.errors.length}):</p>
                    <div className="max-h-20 overflow-y-auto">
                      {autoReplyJobStatus.errors.slice(0, 5).map((err, i) => (
                        <p key={i} className="text-xs text-red-600">{err}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {autoReplyJobStatus && autoReplyJobStatus.status === "running" && (
              <Button variant="outline" onClick={async () => {
                await axios.post(`${API}/mail/jobs/cancel`, { job_id: autoReplyJobStatus.id });
                fetchAutoReplyJobStatus();
              }} className="gap-1 text-red-600">
                <StopCircle className="w-4 h-4" /> Anuluj
              </Button>
            )}
            {autoReplyJobStatus && (autoReplyJobStatus.status === "completed" || autoReplyJobStatus.status === "cancelled") && (
              <Button onClick={() => { setAutoReplyJobStatus(null); fetchFolderStats(currentFolder); }} className="gap-2 bg-green-600 hover:bg-green-700">
                <PlayCircle className="w-4 h-4" /> Nowa wysyłka
              </Button>
            )}
            <Button variant="outline" onClick={() => setAutoReplyJobDialogOpen(false)}>Zamknij</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Niestandardowe foldery */}
      <div className="flex gap-1 flex-wrap items-center">
        {CUSTOM_FOLDERS.map(folder => (
          <Button
            key={folder.id}
            variant={currentFolder === folder.id ? "default" : "outline"}
            size="sm"
            className="gap-1.5 text-xs"
            onClick={() => {
              setCurrentFolder(folder.id);
              // Automatycznie przełącz na odpowiednią zakładkę
              if (folder.id === "wyslane" || folder.id === "odbite") {
                setActiveTab("sent");
              } else {
                setActiveTab("inbox");
              }
            }}
          >
            {getFolderIcon(folder.id)}
            {folder.name}
          </Button>
        ))}
        
        {/* Przycisk Auto-Reply dla folderu */}
        {currentFolder && currentFolder !== "inbox" && currentFolder !== "wyslane" && currentFolder !== "spam" && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs ml-2 text-green-600 border-green-200 hover:bg-green-50"
            onClick={() => { fetchFolderStats(currentFolder); fetchAutoReplyJobStatus(); setAutoReplyJobDialogOpen(true); }}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Odpowiedz wszystkim AI
            {folderStats.unreplied > 0 && (
              <Badge variant="secondary" className="ml-1 text-[10px] bg-orange-100 text-orange-700">
                {folderStats.unreplied}
              </Badge>
            )}
          </Button>
        )}
      </div>

      {/* AI Sorting Config Dialog */}
      <Dialog open={sortingDialogOpen} onOpenChange={setSortingDialogOpen}>
        <DialogContent className="sm:max-w-none">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-500" />
              Konfiguracja sortowania AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Prompt sortowania</Label>
              <Textarea 
                value={sortingConfig.prompt}
                onChange={(e) => setSortingConfig({...sortingConfig, prompt: e.target.value})}
                rows={12}
                className="font-mono text-sm"
                placeholder="Wpisz instrukcje dla AI..."
              />
              <p className="text-xs text-muted-foreground">
                AI przeanalizuje kazdego emaila i przypisze go do odpowiedniego folderu na podstawie tego promptu.
              </p>
            </div>
            
            <div className="bg-muted/50 p-3 rounded-lg">
              <p className="text-xs font-medium mb-2">Dostepne foldery:</p>
              <div className="flex flex-wrap gap-1">
                {CUSTOM_FOLDERS.filter(f => f.id !== "inbox" && f.id !== "wyslane").map(f => (
                  <Badge key={f.id} variant="outline" className="text-xs">{f.id}</Badge>
                ))}
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={sortingConfig.enabled}
                  onChange={(e) => setSortingConfig({...sortingConfig, enabled: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Wlacz sortowanie AI</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSortingDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleSaveSortingConfig} className="gap-2">
              <Check className="w-4 h-4" /> Zapisz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to Folder Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FolderInput className="w-5 h-5" />
              Przenies do folderu
            </DialogTitle>
          </DialogHeader>
          {emailToMove && (
            <div className="py-2">
              <p className="text-sm text-muted-foreground mb-4 truncate">
                "{emailToMove.subject}"
              </p>
              <div className="grid grid-cols-2 gap-2">
                {CUSTOM_FOLDERS.filter(f => f.id !== "inbox" && f.id !== "wyslane").map(folder => (
                  <Button
                    key={folder.id}
                    variant="outline"
                    className="justify-start gap-2"
                    onClick={() => handleMoveEmail(folder.id)}
                  >
                    {getFolderIcon(folder.id)}
                    {folder.name}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Nowa wiadomosc</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Od</Label>
              <Select value={compose.from_account} onValueChange={(v) => setCompose({...compose, from_account: v})}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {accounts.map(acc => (
                    <SelectItem key={acc.id} value={acc.id}>{acc.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Do *</Label>
              <Input value={compose.to} onChange={(e) => setCompose({...compose, to: e.target.value})} placeholder="adres@email.pl" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Temat *</Label>
              <Input value={compose.subject} onChange={(e) => setCompose({...compose, subject: e.target.value})} placeholder="Temat wiadomosci" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tresc</Label>
              <Textarea value={compose.body} onChange={(e) => setCompose({...compose, body: e.target.value})}
                rows={8} className="resize-none" placeholder="Tresc wiadomosci..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)}>Anuluj</Button>
            <Button onClick={handleSend} disabled={sending} className="gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Wyslij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Odpowiedz</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Od</Label>
                <Select value={compose.from_account} onValueChange={(v) => setCompose({...compose, from_account: v})}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>{acc.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Do</Label>
                <Input value={compose.to} onChange={(e) => setCompose({...compose, to: e.target.value})} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Temat</Label>
              <Input value={compose.subject} onChange={(e) => setCompose({...compose, subject: e.target.value})} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Tresc</Label>
              <Textarea value={compose.body} onChange={(e) => setCompose({...compose, body: e.target.value})}
                rows={10} className="resize-none font-mono text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyOpen(false)}>Anuluj</Button>
            <Button onClick={handleSend} disabled={sending} className="gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Wyslij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account Settings Dialog */}
      <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader><DialogTitle>Konfiguracja kont email</DialogTitle></DialogHeader>
          
          {/* Lista istniejacych kont */}
          <div className="space-y-2 mb-4">
            <Label className="text-xs text-muted-foreground">Skonfigurowane konta</Label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {accounts.map(acc => (
                <div key={acc.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {acc.type === "imap" ? <Server className="w-4 h-4 text-blue-500" /> : <Mail className="w-4 h-4 text-green-500" />}
                    <div>
                      <p className="text-sm font-medium">{acc.email}</p>
                      <p className="text-[10px] text-muted-foreground">{acc.type === "imap" ? `IMAP: ${acc.imap_server}` : "Konto systemowe"}</p>
                    </div>
                  </div>
                  {acc.type === "imap" && (
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteMailAccount(acc.id)}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* Formularz dodawania nowego konta */}
          <div className="space-y-4 pt-4">
            <Label className="text-sm font-medium">Dodaj nowe konto IMAP</Label>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Adres email *</Label>
                <Input 
                  value={newAccount.email} 
                  onChange={(e) => setNewAccount({...newAccount, email: e.target.value})}
                  placeholder="biuro@firma.pl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Nazwa wyswietlana</Label>
                <Input 
                  value={newAccount.name} 
                  onChange={(e) => setNewAccount({...newAccount, name: e.target.value})}
                  placeholder="Biuro"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Serwer IMAP *</Label>
                <Input 
                  value={newAccount.imap_server} 
                  onChange={(e) => setNewAccount({...newAccount, imap_server: e.target.value})}
                  placeholder="imap.firma.pl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Port IMAP</Label>
                <Select value={String(newAccount.imap_port)} onValueChange={(v) => setNewAccount({...newAccount, imap_port: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="993">993 (SSL)</SelectItem>
                    <SelectItem value="143">143 (STARTTLS)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Serwer SMTP *</Label>
                <Input 
                  value={newAccount.smtp_server} 
                  onChange={(e) => setNewAccount({...newAccount, smtp_server: e.target.value})}
                  placeholder="smtp.firma.pl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Port SMTP</Label>
                <Select value={String(newAccount.smtp_port)} onValueChange={(v) => setNewAccount({...newAccount, smtp_port: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="587">587 (STARTTLS)</SelectItem>
                    <SelectItem value="465">465 (SSL)</SelectItem>
                    <SelectItem value="25">25 (bez szyfrowania)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Login / Uzytkownik *</Label>
                <Input 
                  value={newAccount.username} 
                  onChange={(e) => setNewAccount({...newAccount, username: e.target.value})}
                  placeholder="biuro@firma.pl"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Haslo *</Label>
                <div className="relative">
                  <Input 
                    type={showPassword ? "text" : "password"}
                    value={newAccount.password} 
                    onChange={(e) => setNewAccount({...newAccount, password: e.target.value})}
                    placeholder="********"
                  />
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={handleTestConnection} disabled={testingConnection} className="gap-2">
                {testingConnection ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                Testuj polaczenie
              </Button>
              <Button onClick={handleAddMailAccount} className="gap-2">
                <Plus className="w-4 h-4" /> Dodaj konto
              </Button>
            </div>

            <p className="text-[10px] text-muted-foreground">
              * Poczta IMAP jest pobierana bez usuwania z serwera. System sledzi ktore maile zostaly przeczytane i na ktore odpowiedziano.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dodaj kontakt</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Email *</Label>
                <Input value={newContact.email} onChange={(e) => setNewContact({...newContact, email: e.target.value})} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Imie</Label>
                <Input value={newContact.name} onChange={(e) => setNewContact({...newContact, name: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Firma</Label>
                <Input value={newContact.company} onChange={(e) => setNewContact({...newContact, company: e.target.value})} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Telefon</Label>
                <Input value={newContact.phone} onChange={(e) => setNewContact({...newContact, phone: e.target.value})} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Kategoria</Label>
              <Select value={newContact.category} onValueChange={(v) => setNewContact({...newContact, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="client">Klient</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                  <SelectItem value="other">Inny</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Notatki</Label>
              <Textarea value={newContact.notes} onChange={(e) => setNewContact({...newContact, notes: e.target.value})} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleAddContact}>Dodaj</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="inbox" className="gap-1.5">
            <Inbox className="w-3.5 h-3.5" /> Odebrane
            <Badge variant="secondary" className="text-[10px] ml-1">{totalEmails > 0 ? `${inbox.length}/${totalEmails}` : inbox.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="sent" className="gap-1.5">
            <Send className="w-3.5 h-3.5" /> Wyslane
            <Badge variant="secondary" className="text-[10px] ml-1">{sent.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="crm" className="gap-1.5">
            <Users className="w-3.5 h-3.5" /> CRM
            <Badge variant="secondary" className="text-[10px] ml-1">{contacts.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Banner informujący o trwającej wysyłce */}
        {autoReplyJobStatus && autoReplyJobStatus.status === "running" && (
          <div className="mt-4 mb-2 p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Bot className="w-6 h-6 text-green-600" />
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping"></span>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Wysyłka AI w trakcie...
                  </p>
                  <p className="text-xs text-green-600">
                    Wysłano: {autoReplyJobStatus.sent || 0} | Błędy: {autoReplyJobStatus.failed || 0} | Pozostało: {(autoReplyJobStatus.total_emails || 0) - (autoReplyJobStatus.processed || 0)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right mr-2">
                  <p className="text-lg font-bold text-green-700">{autoReplyJobStatus.progress || 0}%</p>
                  <p className="text-[10px] text-green-600">postęp</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-1 border-green-300 text-green-700 hover:bg-green-100"
                  onClick={() => setAutoReplyJobDialogOpen(true)}
                >
                  <Eye className="w-3 h-3" /> Szczegóły
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-1 border-red-300 text-red-600 hover:bg-red-50"
                  onClick={async () => {
                    if (window.confirm('Na pewno zatrzymać wysyłkę?')) {
                      await axios.post(`${API}/mail/jobs/cancel`, { job_id: autoReplyJobStatus.id });
                      fetchAutoReplyJobStatus();
                      toast.success('Wysyłka zatrzymana');
                    }
                  }}
                >
                  <StopCircle className="w-3 h-3" /> Stop
                </Button>
              </div>
            </div>
            <Progress value={autoReplyJobStatus.progress || 0} className="h-1.5 mt-2" />
          </div>
        )}

        {/* INBOX */}
        <TabsContent value="inbox" className="mt-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Szukaj w mailach..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer whitespace-nowrap">
              <input 
                type="checkbox" 
                checked={showOnlyAnswered}
                onChange={(e) => setShowOnlyAnswered(e.target.checked)}
                className="rounded border-gray-300 w-4 h-4"
              />
              <Reply className="w-4 h-4 text-green-500" />
              <span>Tylko z odpowiedzią</span>
            </label>
            {totalEmails > 0 && (
              <p className="text-xs text-muted-foreground">
                Wyswietlono {inbox.length} z {totalEmails} wiadomosci
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className={selectedEmail ? "" : "lg:col-span-3"}>
              <Card className="rounded-xl border bg-card">
                <CardContent className="p-0">
                  {loading ? (
                    <div className="text-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                    </div>
                  ) : filteredInbox.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Inbox className="w-10 h-10 mx-auto mb-3 opacity-20" />
                      <p className="text-xs">Skrzynka pusta</p>
                    </div>
                  ) : (
                    <>
                      <ScrollArea className="h-[calc(100vh-350px)] min-h-[400px]">
                        <div className="divide-y">
                          {filteredInbox.map((em) => (
                            <button key={`${em.account_id || account}-${em.id}`} onClick={() => { setSelectedEmail(em); setAnalysis(null); }}
                              className={`w-full text-left p-3 hover:bg-accent/30 transition-colors ${selectedEmail?.id === em.id ? "bg-primary/5" : ""}`}>
                              <div className="flex items-start gap-2">
                                <div className="relative">
                                  {em.is_answered ? (
                                    <Reply className="w-4 h-4 mt-0.5 shrink-0 text-green-500" />
                                  ) : (
                                    <Mail className={`w-4 h-4 mt-0.5 shrink-0 ${em.is_read ? "text-gray-400" : "text-blue-500"}`} />
                                  )}
                                  {em.is_foreign && (
                                    <Globe className="w-3 h-3 text-orange-500 absolute -top-1 -right-1" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className={`text-xs truncate ${em.is_read ? "text-muted-foreground" : "font-semibold text-foreground"}`}>
                                      {em.subject || "(bez tematu)"}
                                    </p>
                                    {em.is_answered && (
                                      <Badge variant="outline" className="text-[8px] bg-green-50 text-green-700 shrink-0">
                                        <MailCheck className="w-3 h-3 mr-1" /> Odp. {em.replied_at && new Date(em.replied_at).toLocaleDateString('pl-PL')}
                                      </Badge>
                                    )}
                                    {em.is_foreign && em.detected_language && (
                                      <Badge variant="outline" className="text-[8px] bg-orange-50 text-orange-700 shrink-0">
                                        <Globe className="w-2.5 h-2.5 mr-0.5" />
                                        {LANGUAGE_NAMES[em.detected_language] || em.detected_language.toUpperCase()}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-[10px] text-muted-foreground truncate">{em.from}</p>
                                  <div className="flex items-center gap-2">
                                    <p className="text-[9px] text-muted-foreground">{em.date}</p>
                                    {account === "all" && em.account_email && (
                                      <Badge variant="secondary" className="text-[8px] py-0">
                                        {em.account_email}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </ScrollArea>
                      {inbox.length < totalEmails && (
                        <div className="p-3 border-t">
                          <Button 
                            variant="outline" 
                            className="w-full gap-2" 
                            onClick={loadMoreEmails}
                            disabled={loadingMore}
                          >
                            {loadingMore ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Plus className="w-4 h-4" />
                            )}
                            Zaladuj wiecej ({totalEmails - inbox.length} pozostalo)
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {selectedEmail && (
              <div className="lg:col-span-2 space-y-4">
                <Card className="rounded-xl border bg-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between mb-2">
                      <Button variant="ghost" size="sm" className="gap-1" onClick={() => { setSelectedEmail(null); setTranslatedBody(null); }}>
                        <ChevronLeft className="w-3 h-3" /> Wstecz
                      </Button>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" className="gap-1" onClick={() => handleReply(selectedEmail)}>
                          <Reply className="w-3 h-3" /> Odpowiedz
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-1" 
                          onClick={() => { setEmailToMove(selectedEmail); setMoveDialogOpen(true); }}
                        >
                          <FolderInput className="w-3 h-3" /> Przenies
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-1 text-orange-600 border-orange-200 hover:bg-orange-50" 
                          onClick={() => handleTranslateEmail(selectedEmail)}
                          disabled={translating}
                        >
                          {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
                          Przetlumacz
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-1">
                              <Bot className="w-3 h-3" /> AI
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleGenerateAutoReply(selectedEmail)} disabled={generatingReply}>
                              <MessageSquare className="w-3 h-3 mr-2" /> Generuj odpowiedz AI
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleAnalyzeEmail(selectedEmail)} disabled={analyzing}>
                              <Sparkles className="w-3 h-3 mr-2" /> Analizuj email
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDraftReply(selectedEmail)} disabled={draftingReply}>
                              <Bot className="w-3 h-3 mr-2" /> Wygeneruj szkic
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCreateLeadFromEmail(selectedEmail)}>
                              <UserPlus className="w-3 h-3 mr-2" /> Dodaj do CRM
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(selectedEmail.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <CardTitle className="text-base">{selectedEmail.subject || "(bez tematu)"}</CardTitle>
                      {selectedEmail.is_answered && (
                        <Badge className="bg-green-100 text-green-800 text-[10px]">
                          <MailCheck className="w-3 h-3 mr-1" /> Odpowiedziano
                        </Badge>
                      )}
                      {selectedEmail.custom_folder && (
                        <Badge variant="outline" className="text-[10px]">
                          {CUSTOM_FOLDERS.find(f => f.id === selectedEmail.custom_folder)?.name || selectedEmail.custom_folder}
                        </Badge>
                      )}
                      {selectedEmail.is_foreign && selectedEmail.detected_language && (
                        <Badge className="bg-orange-100 text-orange-800 text-[10px]">
                          <Globe className="w-3 h-3 mr-1" />
                          {LANGUAGE_NAMES[selectedEmail.detected_language] || selectedEmail.detected_language.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <span>Od: {selectedEmail.from}</span>
                      <span>|</span>
                      <span>Do: {selectedEmail.to}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{selectedEmail.date}</p>
                    {selectedEmail.replied_at && (
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[10px] text-green-600 flex items-center gap-1">
                          <MailCheck className="w-3 h-3" /> Odpowiedziano: {new Date(selectedEmail.replied_at).toLocaleString('pl-PL')}
                        </p>
                        {selectedEmail.is_continuation && (
                          <Badge variant="outline" className="text-[8px] bg-blue-50 text-blue-700">
                            Kontynuacja wątku ({selectedEmail.thread_messages_count || 0} wiadomości)
                          </Badge>
                        )}
                      </div>
                    )}
                  </CardHeader>
                  <CardContent>
                    <Separator className="mb-3" />
                    
                    {/* Wygenerowana odpowiedz AI */}
                    {(generatingReply || generatedReply) && (
                      <div className="mb-4 p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200">
                        <p className="text-xs font-medium text-green-700 mb-2 flex items-center gap-1">
                          <MessageSquare className="w-3.5 h-3.5" /> 
                          {generatingReply ? "Generowanie odpowiedzi..." : "Wygenerowana odpowiedź AI:"}
                        </p>
                        {generatingReply ? (
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                            <span className="text-sm text-green-600">Trwa generowanie...</span>
                          </div>
                        ) : (
                          <>
                            <div className="prose prose-sm max-w-none text-sm text-foreground leading-relaxed whitespace-pre-wrap mb-3">
                              {generatedReply}
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={() => handleUseGeneratedReply(selectedEmail)} className="gap-1">
                                <Reply className="w-3 h-3" /> Uzyj do odpowiedzi
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setGeneratedReply(null)}>
                                Zamknij
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Przetłumaczona treść */}
                    {translatedBody && (
                      <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200">
                        <p className="text-xs font-medium text-orange-700 mb-2 flex items-center gap-1">
                          <Languages className="w-3.5 h-3.5" /> Przetłumaczona treść:
                        </p>
                        <SafeHtml
                          className="prose prose-sm max-w-none text-sm text-foreground leading-relaxed
                            [&_p]:mb-3 [&_br]:leading-relaxed [&_a]:text-blue-600 [&_a]:underline"
                          html={formatEmailBody(translatedBody)}
                        />
                      </div>
                    )}
                    
                    {/* Oryginalna treść */}
                    {translatedBody && (
                      <p className="text-xs font-medium text-muted-foreground mb-2">Oryginalna treść:</p>
                    )}
                    <SafeHtml
                      className="prose prose-sm max-w-none text-sm text-foreground leading-relaxed
                        [&_p]:mb-3 [&_br]:leading-relaxed [&_a]:text-blue-600 [&_a]:underline
                        [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600
                        [&_hr]:my-4 [&_hr]:border-gray-200"
                      html={formatEmailBody(selectedEmail.body)}
                    />
                  </CardContent>
                </Card>

                {/* AI Analysis Panel */}
                {(analyzing || analysis) && (
                  <Card className="rounded-xl border bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-500" /> Analiza AI
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analyzing ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" /> Analizuje...
                        </div>
                      ) : analysis && (
                        <div className="space-y-3 text-sm">
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline" className={analysis.is_lead ? "bg-yellow-100 text-yellow-800" : ""}>
                              {analysis.is_lead ? "LEAD" : "Nie jest leadem"}
                            </Badge>
                            <Badge variant="outline">Kategoria: {analysis.category}</Badge>
                            <Badge variant="outline" className={
                              analysis.priority === "high" ? "bg-red-100 text-red-800" :
                              analysis.priority === "medium" ? "bg-yellow-100 text-yellow-800" : ""
                            }>
                              Priorytet: {analysis.priority}
                            </Badge>
                          </div>
                          {analysis.summary && (
                            <p className="text-muted-foreground"><strong>Podsumowanie:</strong> {analysis.summary}</p>
                          )}
                          {analysis.suggested_reply && (
                            <div className="p-2 bg-white dark:bg-gray-900 rounded border">
                              <p className="text-xs text-muted-foreground mb-1">Sugerowana odpowiedz:</p>
                              <p className="text-sm">{analysis.suggested_reply}</p>
                            </div>
                          )}
                          {analysis.article_topics?.length > 0 && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Propozycje tematow artykulow:</p>
                              <ul className="list-disc list-inside text-sm">
                                {analysis.article_topics.map((topic, i) => <li key={i}>{topic}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        {/* SENT / BOUNCED */}
        <TabsContent value="sent" className="mt-4">
          {/* Statystyki wysyłek */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Card className={`p-3 cursor-pointer transition-all ${currentFolder === 'wyslane' ? 'ring-2 ring-blue-500' : ''}`}
                  onClick={() => setCurrentFolder('wyslane')}>
              <div className="text-2xl font-bold text-blue-600">{sentStats.total_sent || 0}</div>
              <div className="text-xs text-muted-foreground">Wysłanych</div>
            </Card>
            <Card className="p-3">
              <div className="text-2xl font-bold text-green-600">{sentStats.sent_today || 0}</div>
              <div className="text-xs text-muted-foreground">Dzisiaj</div>
            </Card>
            <Card className="p-3">
              <div className="text-2xl font-bold text-purple-600">{sentStats.auto_replies || 0}</div>
              <div className="text-xs text-muted-foreground">Automatycznych</div>
            </Card>
            <Card className={`p-3 cursor-pointer transition-all ${currentFolder === 'odbite' ? 'ring-2 ring-red-500' : ''}`}
                  onClick={() => setCurrentFolder('odbite')}>
              <div className="text-2xl font-bold text-red-600">{sentStats.total_bounced || 0}</div>
              <div className="text-xs text-muted-foreground">Odbitych</div>
            </Card>
            <Card className="p-3">
              <div className="text-2xl font-bold text-emerald-600">{sentStats.delivery_rate || 100}%</div>
              <div className="text-xs text-muted-foreground">Dostarczalność</div>
            </Card>
          </div>

          {/* Nagłówek z tytułem widoku */}
          <div className="flex items-center gap-2 mb-4">
            {currentFolder === 'odbite' ? (
              <>
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h3 className="text-lg font-semibold text-red-700">Odbite wiadomości</h3>
                <Badge variant="destructive" className="ml-2">{bounced.length}</Badge>
              </>
            ) : (
              <>
                <Send className="w-5 h-5 text-emerald-500" />
                <h3 className="text-lg font-semibold text-emerald-700">Wysłane wiadomości</h3>
                <Badge className="ml-2 bg-emerald-100 text-emerald-700">{sent.length}</Badge>
              </>
            )}
          </div>
          
          <Card className="rounded-xl border bg-card">
            <CardContent className="p-0">
              {/* Widok dla odbitych */}
              {currentFolder === 'odbite' ? (
                bounced.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-500 opacity-50" />
                    <p className="text-xs text-green-600 font-medium">Brak odbitych wiadomości</p>
                    <p className="text-[10px] mt-1">Wszystkie maile zostały poprawnie dostarczone</p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-[500px]">
                    <div className="divide-y">
                      {bounced.map((em) => (
                        <div key={em.id} className="p-3 hover:bg-red-50/50 transition-colors bg-red-50/30">
                          <div className="flex items-start gap-2">
                            <div className="relative">
                              <XCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-semibold text-foreground truncate">{em.subject}</p>
                                <Badge variant="destructive" className="text-[8px] shrink-0">
                                  Niedostarczony
                                </Badge>
                              </div>
                              <p className="text-[10px] text-muted-foreground truncate">Do: {em.to}</p>
                              <p className="text-[10px] text-muted-foreground truncate">Od: {em.from}</p>
                              <p className="text-[9px] text-red-600">Wysłano: {new Date(em.sent_at).toLocaleString('pl-PL')}</p>
                              <p className="text-[9px] text-red-700 font-medium">Odbito: {new Date(em.bounced_at).toLocaleString('pl-PL')}</p>
                              {em.reason && (
                                <p className="text-[9px] text-red-600 mt-1 bg-red-100 px-2 py-0.5 rounded">
                                  Powód: {em.reason}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSelectedSentEmail(em)}>
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={async () => {
                                if (window.confirm('Usuń z listy odbitych?')) {
                                  try {
                                    await axios.delete(`${API}/mail/bounced/${em.id}`);
                                    toast.success('Usunięto');
                                    fetchData();
                                  } catch { toast.error('Błąd'); }
                                }
                              }}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )
              ) : (
                /* Widok dla wysłanych */
                sent.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Send className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-xs">Brak wysłanych wiadomości</p>
                    <p className="text-[10px] mt-1">Wysłane odpowiedzi AI pojawią się tutaj</p>
                  </div>
                ) : (
                  <ScrollArea className="max-h-[500px]">
                    <div className="divide-y">
                      {sent.map((em) => (
                        <div key={em.id} className={`p-3 hover:bg-accent/30 transition-colors ${em.bounced ? 'bg-red-50/50' : ''}`}>
                          <div className="flex items-start gap-2">
                            <div className="relative">
                              <Send className={`w-4 h-4 mt-0.5 shrink-0 ${em.bounced ? 'text-red-500' : 'text-emerald-500'}`} />
                              {em.auto_reply && (
                                <Bot className="w-3 h-3 text-purple-500 absolute -bottom-1 -right-1" />
                              )}
                              {em.bounced && (
                                <XCircle className="w-3 h-3 text-red-500 absolute -top-1 -right-1" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="text-xs font-semibold text-foreground truncate">{em.subject}</p>
                                {em.auto_reply && (
                                  <Badge variant="outline" className="text-[8px] bg-purple-50 text-purple-700 shrink-0">
                                    <Bot className="w-2.5 h-2.5 mr-0.5" /> AI
                                  </Badge>
                                )}
                                {em.is_continuation && (
                                  <Badge variant="outline" className="text-[8px] bg-blue-50 text-blue-700 shrink-0">
                                    Kontynuacja
                                  </Badge>
                                )}
                                {em.bounced && (
                                  <Badge variant="destructive" className="text-[8px] shrink-0">
                                    <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Odbity
                                  </Badge>
                                )}
                              </div>
                              <p className="text-[10px] text-muted-foreground truncate">Do: {em.to}</p>
                              <p className="text-[10px] text-muted-foreground truncate">Od: {em.from}</p>
                              <p className="text-[9px] text-muted-foreground">{new Date(em.sent_at).toLocaleString('pl-PL')}</p>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSelectedSentEmail(em)}>
                                <Eye className="w-3 h-3" />
                              </Button>
                              {!em.bounced && (
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={async () => {
                                  if (window.confirm('Oznacz jako odbity (nie dotarł)?')) {
                                    try {
                                      await axios.post(`${API}/mail/mark-bounced`, { sent_id: em.id, reason: 'Ręczne oznaczenie' });
                                      toast.success('Oznaczono jako odbity');
                                      fetchData();
                                    } catch { toast.error('Błąd'); }
                                  }
                                }}>
                                  <AlertTriangle className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CRM */}
        <TabsContent value="crm" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Ksiazka klientow</h2>
            <Button onClick={() => setContactDialogOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" /> Dodaj kontakt
            </Button>
          </div>
          <Card className="rounded-xl border bg-card">
            <CardContent className="p-0">
              {contacts.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p className="text-xs">Brak kontaktow</p>
                </div>
              ) : (
                <ScrollArea className="max-h-[500px]">
                  <div className="divide-y">
                    {contacts.map((contact) => (
                      <div key={contact.id} className="p-4 hover:bg-accent/30 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <Users className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{contact.name || contact.email}</p>
                              <p className="text-xs text-muted-foreground">{contact.email}</p>
                              {contact.company && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                  <Building2 className="w-3 h-3" /> {contact.company}
                                </p>
                              )}
                              <div className="flex gap-2 mt-2">
                                <Badge className={getCategoryBadge(contact.category)} variant="secondary">
                                  {contact.category}
                                </Badge>
                                {contact.source === "email" && (
                                  <Badge variant="outline" className="text-[10px]">
                                    <Mail className="w-3 h-3 mr-1" /> Z maila
                                  </Badge>
                                )}
                                {contact.has_account && (
                                  <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700">
                                    <CheckCircle className="w-3 h-3 mr-1" /> Ma konto
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="gap-1 text-xs"
                              onClick={() => handleViewContactMessages(contact)}
                            >
                              <History className="w-3 h-3" />
                              Historia
                            </Button>
                            {!contact.has_account && (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="gap-1 text-xs"
                                onClick={() => handleCreateAccountForContact(contact.id)}
                                disabled={creatingAccount === contact.id}
                              >
                                {creatingAccount === contact.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <UserPlus className="w-3 h-3" />
                                )}
                                Zaloz konto
                              </Button>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setCompose({ ...compose, to: contact.email, from_account: account });
                                setComposeOpen(true);
                              }}
                            >
                              <Mail className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        {contact.notes && (
                          <p className="text-xs text-muted-foreground mt-2 pl-13 line-clamp-2">{contact.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog podglądu wysłanego/odbitego maila */}
      <Dialog open={!!selectedSentEmail} onOpenChange={(open) => !open && setSelectedSentEmail(null)}>
        <DialogContent className="max-w-none max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedSentEmail?.bounced ? (
                <XCircle className="w-5 h-5 text-red-500" />
              ) : (
                <Send className="w-5 h-5 text-emerald-500" />
              )}
              {selectedSentEmail?.bounced ? 'Wiadomość odbita' : 'Wysłana wiadomość'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSentEmail && (
            <div className="flex-1 overflow-y-auto space-y-4">
              {/* Metadata */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground w-16">Temat:</span>
                  <span className="text-sm font-semibold">{selectedSentEmail.subject}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground w-16">Do:</span>
                  <span className="text-sm">{selectedSentEmail.to}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground w-16">Od:</span>
                  <span className="text-sm">{selectedSentEmail.from}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground w-16">Wysłano:</span>
                  <span className="text-sm">{new Date(selectedSentEmail.sent_at).toLocaleString('pl-PL')}</span>
                </div>
                {selectedSentEmail.auto_reply && (
                  <div className="flex items-center gap-2">
                    <Bot className="w-4 h-4 text-purple-500" />
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
                      Odpowiedź wygenerowana przez AI
                    </Badge>
                  </div>
                )}
                {selectedSentEmail.is_continuation && (
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-blue-500" />
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
                      Kontynuacja wątku
                    </Badge>
                  </div>
                )}
                {selectedSentEmail.bounced && (
                  <div className="flex items-center gap-2 mt-2 p-2 bg-red-50 rounded">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <div>
                      <p className="text-xs text-red-700 font-medium">Wiadomość nie została dostarczona</p>
                      {selectedSentEmail.bounced_at && (
                        <p className="text-xs text-red-600">Odbito: {new Date(selectedSentEmail.bounced_at).toLocaleString('pl-PL')}</p>
                      )}
                      {selectedSentEmail.reason && (
                        <p className="text-xs text-red-600">Powód: {selectedSentEmail.reason}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Treść wiadomości */}
              <div className="border rounded-lg p-4">
                <h4 className="text-xs font-medium text-muted-foreground mb-2">Treść wiadomości:</h4>
                <SafeHtml
                  className="prose prose-sm max-w-none text-sm
                    [&_p]:mb-3 [&_br]:leading-relaxed [&_a]:text-blue-600 [&_a]:underline
                    [&_hr]:my-4 [&_hr]:border-gray-200"
                  html={formatEmailBody(selectedSentEmail.body)}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSelectedSentEmail(null)}>
              Zamknij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog historii wiadomości kontaktu */}
      <Dialog open={!!selectedContact} onOpenChange={(open) => !open && setSelectedContact(null)}>
        <DialogContent className="max-w-none max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Historia korespondencji
            </DialogTitle>
            {selectedContact && (
              <div className="text-sm text-muted-foreground">
                {selectedContact.name || selectedContact.email} ({selectedContact.email})
              </div>
            )}
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {loadingContactMessages ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="ml-2 text-sm text-muted-foreground">Ładowanie historii...</span>
              </div>
            ) : contactMessages.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-20" />
                <p className="text-sm">Brak wiadomości</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contactMessages.map((msg, idx) => (
                  <div 
                    key={msg.id || idx} 
                    className={`p-3 rounded-lg border ${
                      msg.direction === 'incoming' 
                        ? 'bg-blue-50/50 border-blue-200' 
                        : 'bg-emerald-50/50 border-emerald-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {msg.direction === 'incoming' ? (
                          <ArrowDownLeft className="w-4 h-4 text-blue-600" />
                        ) : (
                          <ArrowUpRight className="w-4 h-4 text-emerald-600" />
                        )}
                        <span className={`text-xs font-medium ${
                          msg.direction === 'incoming' ? 'text-blue-700' : 'text-emerald-700'
                        }`}>
                          {msg.direction === 'incoming' ? 'Otrzymano' : 'Wysłano'}
                        </span>
                        {msg.auto_reply && (
                          <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700">
                            <Bot className="w-2.5 h-2.5 mr-0.5" /> AI
                          </Badge>
                        )}
                        {msg.ai_replied && (
                          <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700">
                            <MailCheck className="w-2.5 h-2.5 mr-0.5" /> Odpowiedziano
                          </Badge>
                        )}
                        {msg.bounced && (
                          <Badge variant="destructive" className="text-[10px]">
                            <XCircle className="w-2.5 h-2.5 mr-0.5" /> Odbity
                          </Badge>
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground">
                        {msg.date ? new Date(msg.date).toLocaleString('pl-PL') : ''}
                      </span>
                    </div>
                    <p className="text-sm font-medium mt-1">{msg.subject}</p>
                    {msg.body && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-3">
                        {msg.body.replace(/<[^>]*>/g, '').substring(0, 200)}...
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mr-auto">
              <span>Otrzymanych: {contactMessages.filter(m => m.direction === 'incoming').length}</span>
              <span>•</span>
              <span>Wysłanych: {contactMessages.filter(m => m.direction === 'outgoing').length}</span>
            </div>
            <Button 
              variant="outline" 
              onClick={() => {
                if (selectedContact) {
                  setCompose({ ...compose, to: selectedContact.email, from_account: account });
                  setSelectedContact(null);
                  setComposeOpen(true);
                }
              }}
            >
              <Mail className="w-4 h-4 mr-1" /> Napisz
            </Button>
            <Button variant="outline" onClick={() => setSelectedContact(null)}>
              Zamknij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
